import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Crown, TrendingUp, DollarSign, Ban, UserCheck, Gift, Search, MessageSquare, CheckCircle2, Sparkles, Copy, X, Download, MinusCircle } from "lucide-react";
import { API, getToken } from "@/lib/api";

export default function AdminPanel() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [grantOpen, setGrantOpen] = useState(null); // user object
  const [tickets, setTickets] = useState([]);
  const [openTickets, setOpenTickets] = useState(0);
  const [aiDraft, setAiDraft] = useState(null); // { ticket, text, loading }

  const loadAll = async (q = "") => {
    try {
      const [s, u, ul, ts] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/usage"),
        api.get(`/admin/users?limit=100${q ? `&search=${encodeURIComponent(q)}` : ""}`),
        api.get("/admin/support"),
      ]);
      setStats(s.data);
      setUsage(u.data);
      setUsers(ul.data.users);
      setTotal(ul.data.total);
      setTickets(ts.data.tickets || []);
      setOpenTickets(ts.data.open_count || 0);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin access required.");
      else toast.error(e?.response?.data?.detail || "Failed to load admin data");
    }
  };

  useEffect(() => { if (user?.is_admin) loadAll(); }, [user]);

  if (authLoading) return <div className="p-10 font-mono text-xs text-zinc-500">// loading</div>;
  if (!user?.is_admin) return <Navigate to="/app" replace />;

  const doSearch = (e) => { e.preventDefault(); loadAll(search); };

  const grantPro = async (userId, days) => {
    try {
      await api.post(`/admin/users/${userId}/grant-pro`, { days });
      toast.success(`Granted ${days}d Pro.`);
      setGrantOpen(null);
      loadAll(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleBan = async (u) => {
    if (!confirm(`${u.banned ? "Un-ban" : "Ban"} ${u.email}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/${u.banned ? "unban" : "ban"}`);
      toast.success(u.banned ? "User unbanned." : "User banned.");
      loadAll(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const revokePro = async (u) => {
    if (!confirm(`Revoke bonus/grant Pro from ${u.email}? (Does not affect paid Stripe subs.)`)) return;
    try {
      const res = await api.post(`/admin/users/${u.id}/revoke-pro`);
      toast.success(`Revoked ${res.data.revoked_count} bonus subscription(s).`);
      loadAll(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const refundUser = async (u) => {
    if (!confirm(`Refund ${u.email}'s most recent Stripe payment AND cancel their subscription?`)) return;
    try {
      const res = await api.post(`/admin/users/${u.id}/refund`);
      const amt = res.data.amount_cents ? `$${(res.data.amount_cents / 100).toFixed(2)}` : "";
      toast.success(`Refunded ${amt}. Subscription cancelled.`);
      loadAll(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };

  const downloadCsv = (path, filename) => {
    const t = getToken();
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  const resolveTicket = async (id) => {
    try {
      await api.post(`/admin/support/${id}/resolve`);
      toast.success("Ticket resolved.");
      loadAll(search);
    } catch (e) { toast.error("Failed"); }
  };

  const openAiDraft = async (t) => {
    setAiDraft({ ticket: t, text: "", loading: true });
    try {
      const res = await api.post(`/admin/support/${t.id}/ai-draft`);
      setAiDraft({ ticket: t, text: res.data.draft, loading: false });
    } catch (e) {
      toast.error("AI draft failed.");
      setAiDraft(null);
    }
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 space-y-10" data-testid="admin-panel">
      <header className="border-b border-white/10 pb-6 flex items-center gap-4">
        <Crown size={28} className="text-signal-yellow" />
        <div>
          <div className="label-eyebrow">// owner console</div>
          <h1 className="heading-display text-4xl sm:text-5xl mt-1">ADMIN.</h1>
          <p className="font-mono text-xs text-zinc-500 mt-2">// full ops view. all metrics live from mongo.</p>
        </div>
      </header>

      {/* Stats grid */}
      {stats && (
        <div className="space-y-4">
          <div className="label-eyebrow">// overview</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              { label: "Total users", value: stats.users.total, testid: "admin-stat-users", icon: Users },
              { label: "MRR (est.)", value: `$${stats.subscriptions.mrr_usd}`, testid: "admin-stat-mrr", icon: DollarSign },
              { label: "Pro (paid)", value: stats.subscriptions.pro_paid, testid: "admin-stat-pro", icon: Crown },
              { label: "Generations 7d", value: stats.usage.generations_7d, testid: "admin-stat-gens", icon: TrendingUp },
              { label: "Signups today", value: stats.users.signups_today, testid: "admin-stat-today" },
              { label: "Signups 7d", value: stats.users.signups_7d, testid: "admin-stat-7d" },
              { label: "Referral bonus", value: stats.subscriptions.pro_bonus, testid: "admin-stat-bonus", icon: Gift },
              { label: "Admin grants", value: stats.subscriptions.admin_grants, testid: "admin-stat-grants" },
            ].map((s) => (
              <div key={s.label} className="bg-[#0A0A0A] p-5" data-testid={s.testid}>
                <div className="flex items-center gap-2 label-eyebrow">
                  {s.icon && <s.icon size={11} className="text-signal-yellow" />}
                  {s.label}
                </div>
                <div className="font-mono text-3xl font-bold mt-2">{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Usage chart */}
      {usage && (
        <div className="space-y-4">
          <div className="label-eyebrow">// usage · last 14 days</div>
          <div className="brutal-border bg-[#141414] p-6">
            <UsageChart data={usage.by_day} />
            <div className="mt-6 grid grid-cols-3 gap-3">
              {["hook", "remix", "score"].map((t) => (
                <div key={t} className="border border-white/10 p-3">
                  <div className="label-eyebrow">{t}</div>
                  <div className="font-mono text-2xl font-bold text-signal-yellow">{usage.by_type[t] || 0}</div>
                </div>
              ))}
            </div>
          </div>
          {usage.top_users && usage.top_users.length > 0 && (
            <div className="brutal-border bg-[#141414]">
              <div className="px-4 py-3 border-b border-white/10 label-eyebrow">// top 10 users by generation count</div>
              <div className="divide-y divide-white/5">
                {usage.top_users.map((u, i) => (
                  <div key={u.user_id} className="flex items-center justify-between px-4 py-2 font-mono text-xs">
                    <span className="text-zinc-500 w-6">#{i + 1}</span>
                    <span className="flex-1 text-zinc-300 truncate">{u.name || u.email}</span>
                    <span className="text-signal-yellow font-bold">{u.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users table */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="label-eyebrow">// users · {total} total</div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="export-users-csv"
              onClick={() => downloadCsv("/admin/export/users.csv", `viralco-users-${new Date().toISOString().slice(0,10)}.csv`)}
              className="border border-white/15 hover:border-signal-yellow hover:text-signal-yellow text-zinc-300 font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-2 transition-colors inline-flex items-center gap-2"
            >
              <Download size={11} /> Users.csv
            </button>
            <button
              data-testid="export-tickets-csv"
              onClick={() => downloadCsv("/admin/export/tickets.csv", `viralco-tickets-${new Date().toISOString().slice(0,10)}.csv`)}
              className="border border-white/15 hover:border-signal-yellow hover:text-signal-yellow text-zinc-300 font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-2 transition-colors inline-flex items-center gap-2"
            >
              <Download size={11} /> Tickets.csv
            </button>
            <form onSubmit={doSearch} className="flex gap-2">
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="admin-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="search email or name..."
                className="bg-[#141414] border border-white/10 text-white placeholder:text-zinc-600 focus:border-signal-yellow focus:outline-none pl-8 pr-3 py-2 font-mono text-xs w-64"
              />
            </div>
            <button data-testid="admin-search-btn" type="submit" className="border border-white/15 hover:border-signal-yellow hover:text-signal-yellow text-zinc-300 font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-2 transition-colors">
              Search
            </button>
            </form>
          </div>
        </div>

        <div className="brutal-border bg-[#141414] overflow-auto" data-testid="admin-users-table">
          <table className="w-full text-xs font-mono min-w-[900px]">
            <thead className="bg-[#0A0A0A] border-b border-white/10">
              <tr className="text-left text-zinc-500 uppercase tracking-wider text-[10px]">
                <th className="p-3">Email</th><th className="p-3">Name</th><th className="p-3">Plan</th>
                <th className="p-3">Referred</th><th className="p-3">Conv.</th>
                <th className="p-3">Joined</th><th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-${u.id}`} className={u.banned ? "opacity-40" : ""}>
                  <td className="p-3 text-zinc-200">
                    {u.email}
                    {u.is_admin && <span className="ml-2 bg-signal-yellow text-black text-[9px] px-1.5 py-0.5 uppercase tracking-wider">ADMIN</span>}
                    {u.banned && <span className="ml-2 bg-signal-red text-white text-[9px] px-1.5 py-0.5 uppercase tracking-wider">BANNED</span>}
                  </td>
                  <td className="p-3 text-zinc-400">{u.name}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                      u.plan === "pro" ? "bg-signal-yellow text-black"
                      : u.plan === "referral_bonus" ? "border border-signal-yellow text-signal-yellow"
                      : u.plan === "admin_grant" ? "border border-white/40 text-white"
                      : "text-zinc-600"
                    }`}>{u.plan}</span>
                  </td>
                  <td className="p-3 text-zinc-400">{u.invited}</td>
                  <td className="p-3 text-signal-yellow">{u.converted}</td>
                  <td className="p-3 text-zinc-500">{u.created_at?.slice(0, 10)}</td>
                  <td className="p-3 text-right space-x-2">
                    <button
                      data-testid={`admin-grant-${u.id}`}
                      onClick={() => setGrantOpen(u)}
                      className="text-signal-yellow hover:underline"
                    >Grant</button>
                    {(u.plan === "referral_bonus" || u.plan === "admin_grant") && (
                      <button
                        data-testid={`admin-revoke-${u.id}`}
                        onClick={() => revokePro(u)}
                        className="text-zinc-400 hover:text-signal-red hover:underline"
                      >Revoke</button>
                    )}
                    {u.plan === "pro" && (
                      <button
                        data-testid={`admin-refund-${u.id}`}
                        onClick={() => refundUser(u)}
                        className="text-zinc-400 hover:text-signal-red hover:underline"
                      >Refund</button>
                    )}
                    {!u.is_admin && (
                      <button
                        data-testid={`admin-ban-${u.id}`}
                        onClick={() => toggleBan(u)}
                        className={u.banned ? "text-zinc-400 hover:underline" : "text-signal-red hover:underline"}
                      >{u.banned ? "Unban" : "Ban"}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {grantOpen && (
        <GrantModal user={grantOpen} onClose={() => setGrantOpen(null)} onGrant={grantPro} />
      )}

      {aiDraft && (
        <AiDraftModal
          state={aiDraft}
          onClose={() => setAiDraft(null)}
        />
      )}

      {/* Support Tickets */}
      <div className="space-y-4">
        <div className="label-eyebrow flex items-center gap-2">
          <MessageSquare size={12} className="text-signal-yellow" />
          support inbox · {openTickets} open
        </div>
        {tickets.length === 0 ? (
          <div className="brutal-border p-8 bg-[#141414] text-center" data-testid="tickets-empty">
            <div className="font-mono text-xs text-zinc-500">// no tickets yet.</div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="tickets-list">
            {tickets.map((t) => (
              <div
                key={t.id}
                data-testid={`ticket-${t.id}`}
                className={`brutal-border bg-[#141414] p-4 space-y-2 ${t.status === "resolved" ? "opacity-50" : ""}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`font-mono text-[10px] px-2 py-1 uppercase tracking-wider ${
                      t.status === "open" ? "bg-signal-yellow text-black" : "bg-white/10 text-zinc-400"
                    }`}>{t.status}</span>
                    <span className="font-mono text-xs text-zinc-300">{t.subject}</span>
                    <span className="font-mono text-[10px] text-zinc-500">from {t.name} · {t.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-zinc-600">{new Date(t.created_at).toLocaleString()}</span>
                    {t.status === "open" && (
                      <>
                        <button
                          data-testid={`ticket-ai-${t.id}`}
                          onClick={() => openAiDraft(t)}
                          className="border border-signal-yellow/50 hover:border-signal-yellow text-signal-yellow px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors inline-flex items-center gap-1"
                        >
                          <Sparkles size={11} /> AI draft
                        </button>
                        <button
                          data-testid={`ticket-resolve-${t.id}`}
                          onClick={() => resolveTicket(t.id)}
                          className="border border-white/15 hover:border-signal-yellow hover:text-signal-yellow text-zinc-400 px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors inline-flex items-center gap-1"
                        >
                          <CheckCircle2 size={11} /> Resolve
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <pre className="font-mono text-xs text-zinc-200 whitespace-pre-wrap break-words border-l-2 border-signal-yellow pl-4 max-h-32 overflow-auto">{t.message}</pre>
                <div className="font-mono text-[10px] text-zinc-600">
                  ↳ reply via: <a href={`mailto:${t.email}?subject=Re: ${encodeURIComponent(t.subject)}`} className="text-signal-yellow hover:underline">{t.email}</a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UsageChart({ data }) {
  if (!data || data.length === 0) return <div className="font-mono text-xs text-zinc-500">// no usage yet</div>;
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-32" data-testid="admin-usage-chart">
      {data.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
          <div
            className="w-full bg-signal-yellow/80 hover:bg-signal-yellow transition-colors relative"
            style={{ height: `${(d.count / max) * 100}%`, minHeight: "2px" }}
            title={`${d.date}: ${d.count}`}
          >
            <span className="absolute -top-5 left-1/2 -translate-x-1/2 font-mono text-[9px] text-zinc-400 opacity-0 group-hover:opacity-100">{d.count}</span>
          </div>
          <span className="font-mono text-[9px] text-zinc-600">{d.date.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

function GrantModal({ user, onClose, onGrant }) {  const [days, setDays] = useState(30);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="brutal-border-strong bg-[#141414] p-6 max-w-md w-full space-y-4" data-testid="grant-modal" onClick={(e) => e.stopPropagation()}>
        <div className="label-eyebrow">// grant pro</div>
        <h3 className="heading-display text-2xl">GRANT PRO TO {user.name.toUpperCase()}</h3>
        <p className="font-mono text-xs text-zinc-400">{user.email}</p>
        <label className="block space-y-2">
          <span className="label-eyebrow block">Days</span>
          <input
            data-testid="grant-days"
            type="number" min={1} max={3650} value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-full bg-black border border-white/10 text-white p-3 font-mono text-lg focus:border-signal-yellow focus:outline-none"
          />
        </label>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="border border-white/15 hover:border-white/40 text-zinc-300 font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2 transition-colors">Cancel</button>
          <button
            data-testid="grant-confirm"
            onClick={() => onGrant(user.id, days)}
            className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-2 hover:bg-signal-yellow-hover transition-colors"
          >Grant {days}d →</button>
        </div>
      </div>
    </div>
  );
}

function AiDraftModal({ state, onClose }) {
  const { ticket, text, loading } = state;
  const [edited, setEdited] = React.useState(text);

  React.useEffect(() => { setEdited(text || ""); }, [text]);

  const copy = async () => {
    await navigator.clipboard.writeText(edited);
    toast.success("Draft copied to clipboard.");
  };

  const mailto = `mailto:${ticket.email}?subject=${encodeURIComponent("Re: " + ticket.subject)}&body=${encodeURIComponent(edited)}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="brutal-border-strong bg-[#141414] p-6 max-w-2xl w-full space-y-4 max-h-[90vh] overflow-auto"
        data-testid="ai-draft-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="label-eyebrow flex items-center gap-2">
              <Sparkles size={12} className="text-signal-yellow" /> AI draft · Claude Sonnet 4.5
            </div>
            <h3 className="heading-display text-2xl mt-2">REPLY TO {ticket.name?.toUpperCase()}</h3>
            <p className="font-mono text-[10px] text-zinc-500 mt-1">re: {ticket.subject} · {ticket.email}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center font-mono text-xs text-zinc-400 animate-pulse">// drafting reply...</div>
        ) : (
          <textarea
            data-testid="ai-draft-text"
            value={edited}
            onChange={(e) => setEdited(e.target.value)}
            className="w-full bg-black border border-white/10 text-white p-4 font-mono text-sm min-h-[280px] focus:border-signal-yellow focus:outline-none whitespace-pre-wrap"
          />
        )}

        <div className="flex flex-wrap gap-2 justify-end">
          <button
            onClick={copy}
            disabled={loading || !edited}
            data-testid="ai-draft-copy"
            className="border border-white/15 hover:border-white/40 text-zinc-300 font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2 transition-colors disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Copy size={12} /> Copy
          </button>
          <a
            href={mailto}
            data-testid="ai-draft-send"
            className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-2 hover:bg-signal-yellow-hover transition-colors inline-flex items-center gap-2"
          >
            Open in email →
          </a>
        </div>
      </div>
    </div>
  );
}

